import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';

import {
  AssetSelectionItem,
  clearAssetSelection,
  setAssetSelection,
  store,
  unselectAllRefrigerationUnitModels,
} from '@/components/pages/EmissionsDashboard';

import { useRawDataDownload } from '@/hooks';

import { AssetSearchDialog } from './AssetSearchDialog';

function AssetSearchButton() {
  const { t } = useTranslation();
  const snap = useSnapshot(store);
  const rawData = useRawDataDownload();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectAsset = (asset: AssetSelectionItem) => {
    setAssetSelection([asset]);
    setOpen(false);
  };

  const handleClearSelection = () => {
    unselectAllRefrigerationUnitModels();
    clearAssetSelection();
    handleCloseMenu();
  };

  const handleDownloadRawData = () => {
    const objectURL = URL.createObjectURL(
      new Blob([JSON.stringify(rawData)], {
        type: 'application/json',
      }),
    );
    const link = document.createElement('a');
    link.download = `${store.selectedAssets[0].id}.json`;
    link.href = objectURL;
    link.click();

    handleCloseMenu();
  };

  const handleNewSearch = () => {
    handleCloseMenu();
    handleOpenDialog();
  };

  return (
    <>
      {snap.selectedAssets.length > 0 ? (
        <>
          <Button
            size="small"
            variant="contained"
            color={snap.isAveragePowerMode ? 'primary' : 'secondary'}
            endIcon={<ArrowDropDownIcon />}
            onClick={handleOpenMenu}
            sx={{ textTransform: 'none' }}
          >
            {snap.selectedAssets[0].id.toUpperCase()}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 250,
                },
              },
            }}
          >
            <MenuItem onClick={handleDownloadRawData}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download raw data</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleClearSelection}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Clear selection</ListItemText>
            </MenuItem>
            <Divider flexItem />
            <MenuItem onClick={handleNewSearch}>
              <ListItemIcon>
                <SearchIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>New search</ListItemText>
            </MenuItem>
          </Menu>
        </>
      ) : (
        <Button
          variant="outlined"
          size="small"
          endIcon={<SearchIcon />}
          onClick={handleOpenDialog}
          sx={{ textWrap: 'nowrap' }}
        >
          {t('searchAssets')}
        </Button>
      )}
      <AssetSearchDialog
        open={open}
        onClose={handleCloseDialog}
        onSelectAsset={handleSelectAsset}
      />
    </>
  );
}
export { AssetSearchButton };
